import * as moment from 'moment';
import { TDrawModel } from '../../draw.model';
import { TTimelieWrapper } from './draw.tasks';
import { TTimelineModel } from '../../timeline.types';
import { DRAW_MODEL_UTILS } from '../utils';

const drawMonths = (drawModel: TDrawModel, header: ReturnType<typeof drawHeader>) => {
  const months = header
    .selectAll('.months')
    .data([drawModel])
    .join('svg')
    .attr('width', (d) => d.innerWidth)
    .classed('months', true);

  months
    .selectAll<d3.BaseType, TTimelineModel['months'][number]>('g')
    .data(drawModel.timelineModel.months, (c) => c.start)
    .join((enter) => {
      const group = enter.append('g');
      group
        .append('text')
        .attr('fill', 'black')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'text-before-edge')
        .style('font-size', '14px')
        .text((d) => moment(d.start).format('MMM yyyy'));

      return group;
    })
    .attr('transform', (d) => {
      const [startPostion] = DRAW_MODEL_UTILS.getRangePosition({ startDate: d.start, endDate: d.end }, drawModel);
      return 'translate(' + startPostion + ', 0)';
    });
  return months;
};

const drawDates = (
  drawModel: TDrawModel,
  header: ReturnType<typeof drawHeader>,
  onDateClick?: (date: string) => void,
) => {
  const dates = header
    .selectAll('.dates')
    .data([drawModel])
    .join('svg')
    .attr('width', (d) => d.innerWidth)
    .classed('dates', true);

  dates
    .selectAll<d3.BaseType, TTimelineModel['timeline'][number]>('g')
    .data(drawModel.timelineModel.timeline, (c) => c.date)
    .join((enter) => {
      const group = enter.append('g');
      group
        .append('rect')
        .attr('width', drawModel.layout.widthOfDay)
        .attr('transform', `translate(-${drawModel.layout.widthOfDay / 2}, 0)`)
        .attr('rx', '4')
        .attr('ry', '4')
        .attr('height', '40')
        .classed('active-date', true);
      group
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'text-before-edge')
        .attr('font-size', '12px')
        .style('fill', (d) => (d.date === moment().format('YYYY-MM-DD') ? 'white' : 'black'))
        .text((d) => d.dayLetter);

      group
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'text-before-edge')
        .style('fill', (d) => (d.date === moment().format('YYYY-MM-DD') ? 'white' : 'black'))
        .attr('transform', 'translate(0, 20)')
        .text((d) => d.day);
      return group;
    })
    .style('fill', (d) => (d.date === moment().format('YYYY-MM-DD') ? '#0F31A9' : 'white'))
    .style('cursor', onDateClick ? 'pointer' : 'default')
    .attr('transform', (d) => {
      const xPostion = DRAW_MODEL_UTILS.getPostionByDate(d.date, drawModel);
      return `translate(${xPostion}, 20)`;
    });

  const dateGroups = dates.selectAll<d3.BaseType, TTimelineModel['timeline'][number]>('g');
  if (onDateClick) {
    dateGroups.on('click.date_click', (_, d) => onDateClick(d.date));
  } else {
    dateGroups.on('click.date_click', null);
  }

  dates
    .selectAll('.active-date')
    .attr('width', () => (drawModel.layout.widthOfDay < 25 ? 25 : drawModel.layout.widthOfDay))
    .attr('transform', `translate(-${drawModel.layout.widthOfDay < 25 ? 25 / 2 : drawModel.layout.widthOfDay / 2}, 0)`);

  return dates;
};

const drawEndOfMonthLine = (drawModel: TDrawModel, header: ReturnType<typeof drawHeader>) => {
  return header
    .selectAll('.end-of-months')
    .data(drawModel.timelineModel.endOfMonths)
    .join('line')
    .style('stroke', '#E3E3E3')
    .style('stroke-width', 1)
    .attr('x1', (d) => DRAW_MODEL_UTILS.getPostionByDate(d, drawModel) + drawModel.layout.widthOfDay / 2)
    .attr('y1', 0)
    .attr('x2', (d) => DRAW_MODEL_UTILS.getPostionByDate(d, drawModel) + drawModel.layout.widthOfDay / 2)
    .attr('y2', drawModel.layout.headerHeight)

    .classed('end-of-months', true);
};

export const drawHeader = (
  drawModel: TDrawModel,
  timelineWrapper: TTimelieWrapper,
  onDateClick?: (date: string) => void,
) => {
  const header = timelineWrapper
    .selectAll('.header')
    .data([drawModel])
    .join('svg')
    .classed('header', true)
    .style('position', 'sticky')
    .style('display', 'block')
    .style('top', '0')
    .style('left', (d) => d.layout.margin.left)
    .style('z-index', '10')
    .style('background-color', 'white')
    .style('border-bottom', '1px solid #e1e1e1')
    .attr('width', (d) => d.innerWidth)
    .attr('height', (d) => d.layout.headerHeight);

  drawMonths(drawModel, header);
  drawDates(drawModel, header, onDateClick);
  drawEndOfMonthLine(drawModel, header);
  return header;
};
